import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Pencil, Plus, Power, RefreshCw } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Cargando,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  actualizarPlan,
  crearPlan,
  generarPreventivas,
  listarActivos,
  listarPlanes,
  type PlanMantenimientoCreate,
  type PlanMantenimientoRead,
} from "./api";

export function PlanesMantenimientoPage(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<PlanMantenimientoRead | "new" | null>(null);

  const planesQ = useQuery({ queryKey: ["mantenimiento", "planes"], queryFn: listarPlanes });
  const activosQ = useQuery({ queryKey: ["mantenimiento", "activos"], queryFn: listarActivos });

  const activoNombre = useMemo(() => {
    const m = new Map<number, string>();
    for (const a of activosQ.data ?? []) m.set(a.id, a.nombre);
    return m;
  }, [activosQ.data]);

  const generar = useMutation({
    mutationFn: generarPreventivas,
    onSuccess: (r) => {
      toast.success(
        r.creadas > 0
          ? `${r.creadas} orden(es) preventiva(s) generada(s)`
          : "No hay preventivas por generar hoy",
      );
      void qc.invalidateQueries({ queryKey: ["mantenimiento", "ordenes"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "No se pudo generar"),
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Planes de mantención"
        icon={CalendarClock}
        subtitle={
          <>
            Mantención preventiva recurrente sobre un activo. El sistema genera la orden de
            trabajo automáticamente cuando se acerca la próxima ejecución (o toca “Generar”
            para adelantarla).
            {planesQ.data && <> ({planesQ.data.length} en total)</>}
          </>
        }
        actions={
          <>
            <Button
              variant="secondary"
              icon={RefreshCw}
              onClick={() => generar.mutate()}
              disabled={generar.isPending}
            >
              {generar.isPending ? "Generando…" : "Generar OT preventivas"}
            </Button>
            <Button icon={Plus} onClick={() => setEditing("new")}>
              Nuevo plan
            </Button>
          </>
        }
      />

      {planesQ.isPending && <Cargando />}
      {planesQ.isError && (
        <ErrorCarga error={planesQ.error} onReintentar={() => planesQ.refetch()} />
      )}

      {planesQ.data && planesQ.data.length === 0 && (
        <EmptyState
          icon={CalendarClock}
          title="Sin planes de mantención"
          description="Define una rutina preventiva (ej. limpieza trimestral del estanque) y el sistema generará las órdenes de trabajo solo."
        />
      )}

      {planesQ.data && planesQ.data.length > 0 && (
        <TableWrap label="Planes de mantención">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Plan</th>
                <th scope="col" className="px-3 py-2 text-left">Activo</th>
                <th scope="col" className="px-3 py-2 text-right">Frecuencia</th>
                <th scope="col" className="px-3 py-2 text-right">Aviso</th>
                <th scope="col" className="px-3 py-2 text-left">Estado</th>
                <th scope="col" className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {planesQ.data.map((p) => (
                <tr key={p.id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-neutral-900">{p.nombre}</div>
                    {p.descripcion && (
                      <div className="text-xs text-neutral-500">{p.descripcion}</div>
                    )}
                    {p.requiere_parada_planta && (
                      <Badge tone="amber">Requiere parada de planta</Badge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {p.activo_id != null ? (activoNombre.get(p.activo_id) ?? "—") : "Por tipo"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">cada {p.frecuencia_dias} d</td>
                  <td className="px-3 py-2 text-right tabular-nums">{p.alerta_dias_antes} d antes</td>
                  <td className="px-3 py-2">
                    <Badge tone={p.activo ? "emerald" : "slate"}>
                      {p.activo ? "Activo" : "Inactivo"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="link"
                        size="sm"
                        icon={Pencil}
                        onClick={() => setEditing(p)}
                      >
                        Editar
                      </Button>
                      <TogglePlan plan={p} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {editing && (
        <PlanForm
          plan={editing === "new" ? null : editing}
          activos={activosQ.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function TogglePlan({ plan }: { plan: PlanMantenimientoRead }): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const mut = useMutation({
    mutationFn: () => actualizarPlan(plan.id, { activo: !plan.activo }),
    onSuccess: () => {
      toast.success(plan.activo ? "Plan desactivado" : "Plan activado");
      void qc.invalidateQueries({ queryKey: ["mantenimiento", "planes"] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : "No se pudo"),
  });
  return (
    <Button
      variant={plan.activo ? "linkDanger" : "link"}
      size="sm"
      icon={Power}
      onClick={() => mut.mutate()}
      disabled={mut.isPending}
    >
      {plan.activo ? "Desactivar" : "Activar"}
    </Button>
  );
}

function PlanForm({
  plan,
  activos,
  onClose,
}: {
  plan: PlanMantenimientoRead | null;
  activos: { id: number; nombre: string }[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const editando = plan !== null;

  const [nombre, setNombre] = useState(plan?.nombre ?? "");
  const [descripcion, setDescripcion] = useState(plan?.descripcion ?? "");
  const [activoId, setActivoId] = useState(plan?.activo_id != null ? String(plan.activo_id) : "");
  const [frecuencia, setFrecuencia] = useState(String(plan?.frecuencia_dias ?? 90));
  const [alerta, setAlerta] = useState(String(plan?.alerta_dias_antes ?? 7));
  const [protocolo, setProtocolo] = useState(plan?.protocolo ?? "");
  const [parada, setParada] = useState(plan?.requiere_parada_planta ?? false);
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: (p: PlanMantenimientoCreate) =>
      editando ? actualizarPlan(plan.id, p) : crearPlan(p),
    onSuccess: () => {
      toast.success(editando ? "Plan actualizado" : "Plan creado");
      void qc.invalidateQueries({ queryKey: ["mantenimiento", "planes"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo guardar";
      setError(msg);
      toast.error(msg);
    },
  });

  function submit(): void {
    setError(null);
    if (!nombre.trim()) return setError("Nombre requerido");
    if (!activoId) return setError("Elige el activo del plan");
    const f = Number(frecuencia);
    if (!Number.isFinite(f) || f <= 0) return setError("La frecuencia debe ser mayor a 0");
    guardar.mutate({
      nombre: nombre.trim(),
      descripcion: descripcion.trim() || null,
      activo_id: Number(activoId),
      frecuencia_dias: f,
      alerta_dias_antes: Number(alerta) || 0,
      protocolo: protocolo.trim() || null,
      requiere_parada_planta: parada,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    nombre, descripcion, activoId, frecuencia, alerta, protocolo, parada,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    guardar.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={editando ? "Editar plan" : "Nuevo plan de mantención"}
      icon={CalendarClock}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={guardar.isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={guardar.isPending}>
            {guardar.isPending ? "Guardando…" : editando ? "Guardar cambios" : "Crear plan"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Input label="Nombre" required value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus />
        <Select label="Activo" value={activoId} onChange={(e) => setActivoId(e.target.value)}>
          <option value="">— Elegir activo —</option>
          {activos.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nombre}
            </option>
          ))}
        </Select>
        <Input
          label="Frecuencia (días)"
          type="number"
          min={1}
          value={frecuencia}
          onChange={(e) => setFrecuencia(e.target.value)}
        />
        <Input
          label="Avisar (días antes)"
          type="number"
          min={0}
          value={alerta}
          onChange={(e) => setAlerta(e.target.value)}
        />
      </div>
      <div className="mt-3">
        <Input label="Descripción" value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </div>
      <div className="mt-3">
        <Textarea
          label="Protocolo (pasos a seguir)"
          value={protocolo}
          onChange={(e) => setProtocolo(e.target.value)}
          rows={3}
        />
      </div>
      <label className="mt-3 inline-flex items-center gap-2 text-sm text-neutral-700">
        <input
          type="checkbox"
          checked={parada}
          onChange={(e) => setParada(e.target.checked)}
          className="h-4 w-4 rounded border-neutral-450"
        />
        Requiere parada de planta
      </label>
      {error && (
        <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}
      {dialogoDescarte}
    </Modal>
  );
}
